import { ref, computed, onMounted, onUnmounted, watch, provide, inject } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { useLoungesStore } from '@/store/lounges';

const QUIZ_WIZARD_KEY = Symbol('quizWizard');

export const STEP_AIRPORTS = 0;
export const STEP_DESTINATIONS = 1;
export const STEP_BUSINESS = 2;
export const STEP_PLAN = 3;
export const STEP_COMPLETE = 4;
const TOTAL_STEPS = 4;

export function provideQuizWizard(options = {}) {
    const {
        startStep = 0,
        planVariant = 'default',
        products = [],
        priorData = {},
        skipPlanStep = false,
    } = options;

    const loungesStore = useLoungesStore();
    const page = usePage();

    // Core wizard state
    const step = ref(
        Math.min(Math.max(0, Number(startStep) || 0), STEP_COMPLETE)
    );
    const saving = ref(false);
    const errors = ref([]);
    const isNavigatingBack = ref(false);

    // Airport state (step 0)
    const selectedAirports = ref([]);
    const airportInputs = ref(1);
    const airportQuery = ref('');
    const attemptAirport = ref(false);
    const airportHasError = computed(
        () => attemptAirport.value && selectedAirports.value.filter((a) => a && a.code).length === 0
    );

    // Destination state (step 1)
    const destinationInputs = ref(1);
    const destRenderKey = ref(0);
    const hasGoogle = ref(false);
    const attemptSubmit = ref(false);
    const destFields = ref([{ place: null }, { place: null }, { place: null }]);
    const destTouched = ref([false, false, false]);
    const destDrafts = ref(['', '', '']);

    // Business state (step 2)
    const hasBusinessAnswer = ref(null);
    const businessEntitiesForm = ref([
        { name: '', type: '', tax_id_type: '', annual_business_spend: '' },
    ]);
    const attemptBusinessSubmit = ref(false);

    // Redirect & timers
    const successRedirectUrl = '/email/verification-notification';
    const emailBlockTimer = ref(null);
    let handleBeforeUnload = null;

    // MY1-3000: entitled members never see the plan step, so it must not count
    // toward the progress bar either.
    const totalSteps = skipPlanStep ? TOTAL_STEPS - 1 : TOTAL_STEPS;

    const progressPercent = computed(() => {
        const idx = Math.min(step.value, totalSteps - 1);
        return Math.round(((idx + 1) / totalSteps) * 100);
    });

    /**
     * Leave the business step for whatever comes next.
     *
     * For entitled members that's straight to the success screen — and we persist
     * completion here rather than relying on the success screen's goToApp(),
     * because /quiz/skip-quiz doesn't set quiz_completed_at and abandoning the
     * tab would otherwise drop them back into the quiz on their next login.
     */
    async function advancePastBusiness() {
        if (!skipPlanStep) {
            step.value = STEP_PLAN;
            return;
        }

        try {
            await window.axios.post('/quiz/complete');
        } catch (e) {}

        step.value = STEP_COMPLETE;
    }

    function hydrateFromPriorData() {
        const prior = priorData || {};

        if (Array.isArray(prior.home_airport_codes) && prior.home_airport_codes.length > 0) {
            selectedAirports.value = prior.home_airport_codes.map((code) => ({
                code,
                name: code,
            }));
            airportInputs.value = Math.min(3, Math.max(1, prior.home_airport_codes.length));
        }

        if (Array.isArray(prior.destinations) && prior.destinations.length > 0) {
            const list = prior.destinations.slice(0, 3);
            destFields.value = [
                { place: list[0] ? { formattedAddress: list[0] } : null },
                { place: list[1] ? { formattedAddress: list[1] } : null },
                { place: list[2] ? { formattedAddress: list[2] } : null },
            ];
            destDrafts.value = [list[0] || '', list[1] || '', list[2] || ''];
            destinationInputs.value = list.length;
        }

        if (prior.has_businesses) {
            hasBusinessAnswer.value = true;
            if (Array.isArray(prior.businesses) && prior.businesses.length > 0) {
                businessEntitiesForm.value = prior.businesses.map((b) => ({
                    name: b.name || '',
                    type: b.type || '',
                    tax_id_type: b.tax_id_type || '',
                    annual_business_spend: b.annual_business_spend
                        ? String(b.annual_business_spend)
                        : '',
                }));
            }
        }
    }

    // Navigation
    function prev() {
        if (step.value <= 0) return;
        isNavigatingBack.value = true;

        if (step.value === STEP_BUSINESS && hasBusinessAnswer.value === true) {
            hasBusinessAnswer.value = null;
            businessEntitiesForm.value = [
                { name: '', type: '', tax_id_type: '', annual_business_spend: '' },
            ];
            attemptBusinessSubmit.value = false;
            setTimeout(() => { isNavigatingBack.value = false; }, 100);
            return;
        }

        step.value -= 1;

        // MY1-3000: don't let a back-step land on the plan step we skipped.
        if (skipPlanStep && step.value === STEP_PLAN) {
            step.value = STEP_BUSINESS;
        }

        if (step.value === STEP_BUSINESS) {
            hasBusinessAnswer.value = null;
            businessEntitiesForm.value = [
                { name: '', type: '', tax_id_type: '', annual_business_spend: '' },
            ];
            attemptBusinessSubmit.value = false;
        }

        setTimeout(() => { isNavigatingBack.value = false; }, 100);
    }

    // Airport functions
    function onAirportSelected(obj, index = 0) {
        selectedAirports.value[index] = obj;
        if (obj && obj.code) {
            attemptAirport.value = false;
            errors.value = [];
        }
    }

    function onAirportChange(val, index = 0) {
        airportQuery.value = val || '';
    }

    async function saveAirport() {
        saving.value = true;
        errors.value = [];
        attemptAirport.value = true;

        const airportCodes = selectedAirports.value
            .filter((a) => a && a.code)
            .map((a) => a.code);

        if (airportCodes.length === 0) {
            saving.value = false;
            errors.value.push({ message: 'Please select at least one home airport' });
            return;
        }

        try {
            await window.axios.post('/quiz/save-home-airport', { home_airports: airportCodes });
            errors.value = [];
            step.value = STEP_DESTINATIONS;
        } catch (e) {
            errors.value.push({ message: 'Failed to save airports' });
        } finally {
            saving.value = false;
        }
    }

    async function skipAirport() {
        try {
            await window.axios.post('/quiz/skip-step', { step: STEP_AIRPORTS, step_name: 'home_airport' });
        } catch (e) {}
        step.value = STEP_DESTINATIONS;
    }

    // Destination functions
    function onDestPlaceChanged(index, place) {
        if (!place) return;
        destFields.value[index].place = {
            formattedAddress: place.formattedAddress || place.description || place.name || '',
            city: place.city || '',
            state: place.state || '',
            country: place.country || '',
        };
        if (errors.value.length > 0) {
            errors.value = [];
        }
    }

    function shiftDestDown(index) {
        for (let i = index; i < destinationInputs.value - 1; i++) {
            destFields.value[i].place = destFields.value[i + 1].place;
            destDrafts.value[i] = destDrafts.value[i + 1];
            destTouched.value[i] = destTouched.value[i + 1];
        }
        destFields.value[destinationInputs.value - 1].place = null;
        destDrafts.value[destinationInputs.value - 1] = '';
        destTouched.value[destinationInputs.value - 1] = false;
        destinationInputs.value--;
    }

    function onDestPlaceCleared(index) {
        if (index > 0 && destinationInputs.value > 1) {
            shiftDestDown(index);
        } else {
            destFields.value[index].place = null;
        }
    }

    function markTouched(index) {
        destTouched.value[index] = true;
    }

    function addDestinationText(index) {
        const value = (destDrafts.value[index] || '').toString().trim();
        if (!value) return;
        destFields.value[index].place = { formattedAddress: value };
        destDrafts.value[index] = '';
        if (errors.value.length > 0) {
            errors.value = [];
        }
    }

    function clearDest(index) {
        if (index > 0 && destinationInputs.value > 1) {
            shiftDestDown(index);
        } else {
            destFields.value[index].place = null;
            destDrafts.value[index] = '';
        }
    }

    async function saveDestinations() {
        attemptSubmit.value = true;
        errors.value = [];
        saving.value = true;
        try {
            const count = destinationInputs.value;
            if (!hasGoogle.value) {
                for (let i = 0; i < count; i++) {
                    const draft = (destDrafts.value[i] || '').toString().trim();
                    if (draft && !destFields.value[i].place) {
                        destFields.value[i].place = { formattedAddress: draft };
                    }
                }
            }
            const out = destFields.value
                .slice(0, count)
                .map((f) => f.place?.formattedAddress)
                .filter((v) => !!v);
            if (out.length === 0) {
                for (let i = 0; i < count; i++) destTouched.value[i] = i === 0;
                errors.value.push({ message: 'Please enter at least one destination' });
                saving.value = false;
                return;
            }

            await window.axios.post('/quiz/save-destinations', { destinations: out });
            errors.value = [];
            step.value = STEP_BUSINESS;
        } catch (e) {
        } finally {
            saving.value = false;
        }
    }

    async function skipDestinations() {
        try {
            await window.axios.post('/quiz/skip-step', { step: STEP_DESTINATIONS, step_name: 'destinations' });
        } catch (e) {}
        errors.value = [];
        attemptSubmit.value = false;
        for (let i = 0; i < destinationInputs.value; i++) destTouched.value[i] = false;
        step.value = STEP_BUSINESS;
    }

    // Business functions
    function formatSpendValue(value) {
        let cleaned = value.toString().replace(/[^0-9.]/g, '');
        let parts = cleaned.split('.');
        if (parts.length > 2) {
            cleaned = parts[0] + '.' + parts.slice(1).join('');
            parts = cleaned.split('.');
        }
        if (parts.length === 2 && parts[1].length > 2) {
            cleaned = parts[0] + '.' + parts[1].substring(0, 2);
            parts = cleaned.split('.');
        }
        if (cleaned) {
            const [whole, decimal] = cleaned.split('.');
            const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return decimal !== undefined ? formatted + '.' + decimal : formatted;
        }
        return '';
    }

    function addBusinessEntity() {
        businessEntitiesForm.value.push({
            name: '', type: '', tax_id_type: '', annual_business_spend: '',
        });
    }

    function removeBusinessEntity(index) {
        if (businessEntitiesForm.value.length > 1) {
            businessEntitiesForm.value.splice(index, 1);
        }
    }

    function formatBusinessSpend(index) {
        const value = businessEntitiesForm.value[index].annual_business_spend;
        if (!value) return;
        businessEntitiesForm.value[index].annual_business_spend = formatSpendValue(value);
    }

    async function saveBusinessEntities() {
        saving.value = true;
        errors.value = [];
        attemptBusinessSubmit.value = true;

        if (hasBusinessAnswer.value === false) {
            try {
                await window.axios.post('/quiz/skip-step', { step: STEP_BUSINESS, step_name: 'business_entities' });
            } catch (e) {}
            await advancePastBusiness();
            saving.value = false;
            return;
        }

        if (hasBusinessAnswer.value === true) {
            let hasErrors = false;
            for (const entity of businessEntitiesForm.value) {
                if (!entity.name || !entity.type || !entity.tax_id_type) {
                    hasErrors = true;
                    break;
                }
            }

            if (hasErrors) {
                errors.value.push({ message: 'Please fill in all required fields for each business entity' });
                saving.value = false;
                return;
            }

            try {
                const entities = businessEntitiesForm.value.map((entity) => ({
                    name: entity.name,
                    type: entity.type,
                    tax_id_type: entity.tax_id_type,
                    annual_business_spend: entity.annual_business_spend
                        ? parseFloat(entity.annual_business_spend.replace(/,/g, ''))
                        : null,
                }));

                await window.axios.post('/quiz/save-business-entities', { entities });
                errors.value = [];
                await advancePastBusiness();
            } catch (e) {
                errors.value.push({ message: 'Failed to save business entities. Please try again.' });
            }
        } else {
            errors.value.push({ message: 'Please select whether you own or run a business' });
        }

        saving.value = false;
    }

    async function skipBusinessEntities() {
        try {
            await window.axios.post('/quiz/skip-step', { step: STEP_BUSINESS, step_name: 'business_entities' });
        } catch (e) {}
        errors.value = [];
        attemptBusinessSubmit.value = false;
        hasBusinessAnswer.value = null;
        await advancePastBusiness();
    }

    // Plan-choice functions
    async function onPlanSelected({ plan, billing_interval }) {
        saving.value = true;
        errors.value = [];

        const payload = {
            plan,
            billing_interval: plan === 'paid' ? billing_interval : null,
            variant: planVariant || 'default',
        };

        const submit = () => window.axios.post('/quiz/select-plan', payload);

        try {
            try { await window.axios.get('/sanctum/csrf-cookie'); } catch (_) {}

            let response;
            try {
                response = await submit();
            } catch (e) {
                if (e?.response?.status === 419) {
                    await window.axios.get('/sanctum/csrf-cookie');
                    response = await submit();
                } else {
                    throw e;
                }
            }

            const data = response?.data;
            if (plan === 'paid' && data?.redirect) {
                window.location.href = data.redirect;
                return;
            }

            step.value = STEP_COMPLETE;
        } catch (e) {
            const status = e?.response?.status;
            errors.value.push({
                message:
                    status === 419
                        ? 'Your session expired. Please refresh the page and try again.'
                        : 'Something went wrong. Please try again.',
            });
        } finally {
            saving.value = false;
        }
    }

    // Completion
    async function goToApp() {
        try {
            saving.value = true;
            await window.axios.post('/quiz/complete');
        } catch (e) {
        } finally {
            window.location.href = successRedirectUrl;
        }
    }

    // Lifecycle
    onMounted(async () => {
        hydrateFromPriorData();

        try { await loungesStore.getFilterOptions(); } catch (e) {}
        try {
            hasGoogle.value = !!(
                window && window.google && window.google.maps && window.google.maps.importLibrary
            );
        } catch (e) {
            hasGoogle.value = false;
        }

        handleBeforeUnload = () => {
            if (step.value < STEP_COMPLETE) {
                try {
                    const data = JSON.stringify({});
                    const blob = new Blob([data], { type: 'application/json' });
                    navigator.sendBeacon('/quiz/skip-quiz', blob);
                } catch (e) {}
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);

        const rtCheck = () => {
            try {
                const sec = page.props?.security || {};
                const user = page.props?.userData || {};
                const verified = !!user.email_verified;
                const deadline = sec.email_deadline_at ? new Date(sec.email_deadline_at).getTime() : null;
                if (!verified && deadline && Date.now() >= deadline) {
                    router.visit('/verify-block', { replace: true });
                }
            } catch (e) {
                try { console.error('Error in email verification check:', e); } catch (_) {}
            }
        };
        rtCheck();
        if (emailBlockTimer.value) {
            try { clearInterval(emailBlockTimer.value); } catch (_) {}
        }
        emailBlockTimer.value = setInterval(rtCheck, 60000);
    });

    onUnmounted(() => {
        if (handleBeforeUnload) {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }
        if (step.value < STEP_BUSINESS) {
            try { window.axios.post('/quiz/skip-quiz'); } catch (e) {}
        }
        try {
            if (emailBlockTimer.value) {
                clearInterval(emailBlockTimer.value);
                emailBlockTimer.value = null;
            }
        } catch (e) {
            try { console.error('Error clearing email verification timer:', e); } catch (_) {}
        }
    });

    // Watchers
    watch(() => step.value, (n) => {
        if (n === STEP_DESTINATIONS && destinationInputs.value < 1) destinationInputs.value = 1;
        if (n === STEP_DESTINATIONS) destRenderKey.value++;
        attemptSubmit.value = false;
        attemptAirport.value = false;
        attemptBusinessSubmit.value = false;
        errors.value = [];
    });

    watch(() => hasBusinessAnswer.value, async (newVal) => {
        if (newVal === false && step.value === STEP_BUSINESS && !isNavigatingBack.value) {
            try {
                await window.axios.post('/quiz/skip-step', { step: STEP_BUSINESS, step_name: 'business_entities' });
            } catch (e) {}
            await advancePastBusiness();
        }
    });

    const wizard = {
        // Constants
        STEP_AIRPORTS, STEP_DESTINATIONS, STEP_BUSINESS, STEP_PLAN, STEP_COMPLETE,
        // Core
        step, saving, errors, progressPercent, prev,
        // Airport
        selectedAirports, airportInputs, airportQuery, attemptAirport, airportHasError,
        onAirportSelected, onAirportChange, saveAirport, skipAirport,
        // Destinations
        destinationInputs, destRenderKey, hasGoogle, attemptSubmit,
        destFields, destTouched, destDrafts,
        onDestPlaceChanged, onDestPlaceCleared, markTouched, addDestinationText, clearDest,
        saveDestinations, skipDestinations,
        // Business
        hasBusinessAnswer, businessEntitiesForm, attemptBusinessSubmit,
        addBusinessEntity, removeBusinessEntity, formatBusinessSpend,
        saveBusinessEntities, skipBusinessEntities,
        // Plan
        planVariant, products, onPlanSelected,
        // Completion
        goToApp,
    };

    provide(QUIZ_WIZARD_KEY, wizard);
    return wizard;
}

export function useQuizWizard() {
    const wizard = inject(QUIZ_WIZARD_KEY);
    if (!wizard) throw new Error('useQuizWizard() requires provideQuizWizard() in a parent component');
    return wizard;
}
